<template>
  <div class="zone-page invisible-scrollbar">
    <section class="zone-toolbar" :aria-label="$t('infrastructure.zoneFilters')">
      <v-select
        v-model="activeSearch"
        class="zone-status-filter"
        :items="statusItems"
        item-text="text"
        item-value="value"
        :placeholder="$t('infrastructure.status')"
        clearable
        dense
        solo
        flat
        hide-details
        @change="searchDataZone"
      />
      <div class="zone-search">
        <input
          v-model.trim="search"
          type="search"
          :placeholder="$t('infrastructure.search')"
          :aria-label="$t('infrastructure.searchZone')"
          @keydown.enter="searchDataZone"
        />
        <button type="button" :aria-label="$t('infrastructure.search')" @click="searchDataZone">
          <v-icon color="white" size="24">mdi-magnify</v-icon>
        </button>
      </div>
      <button
        class="create-zone"
        type="button"
        @click="OpenDataDialog('create', null)"
      >
        {{ $t("infrastructure.create") }} +
      </button>
    </section>

    <section class="zone-panel">
      <div class="zone-table-wrap">
        <v-data-table
          class="zone-table"
          :headers="headers"
          :items="ListData"
          :items-per-page="itemsPerPage"
          item-key="zoneID"
          hide-default-footer
          disable-sort
        >
          <template v-slot:item.no="{ index }">
            {{ no_run + index + 1 }}
          </template>

          <template v-slot:item.active="{ item }">
            <span :class="['zone-status-chip', { inactive: !item.active }]">
              {{ item.active ? $t("infrastructure.active") : $t("infrastructure.inactive") }}
            </span>
          </template>

          <template v-slot:item.action="{ item }">
            <div class="zone-actions">
              <button
                type="button"
                :aria-label="$t('infrastructure.editNamed', { name: item.zoneName || $t('infrastructure.zone') })"
                @click="OpenDataDialog('edit', item.zoneID)"
              >
                <img
                  class="zone-action-icon zone-action-icon--edit"
                  src="@/assets/icons/brush.svg"
                  alt=""
                  aria-hidden="true"
                />
              </button>
              <button
                class="delete"
                type="button"
                :disabled="!item.active || DeletingZoneID === item.zoneID"
                :aria-label="$t('infrastructure.deleteNamed', { name: item.zoneName || $t('infrastructure.zone') })"
                @click="deleteDataZoneByID(item.zoneID)"
              >
                <img
                  class="zone-action-icon zone-action-icon--delete"
                  src="@/assets/icons/bag.svg"
                  alt=""
                  aria-hidden="true"
                />
              </button>
            </div>
          </template>

          <template v-slot:no-data>
            <div class="zone-empty">{{ $t("infrastructure.noZoneData") }}</div>
          </template>
        </v-data-table>
      </div>

      <footer class="zone-pagination">
        <v-pagination
          v-model="page"
          :length="pageCount"
          :total-visible="3"
          color="#00368e"
          @input="ChangePage(page)"
        />
        <v-select
          v-model="itemsPerPage"
          class="zone-page-size"
          :items="items"
          dense
          solo
          flat
          hide-details
          @input="ChangePerPage(itemsPerPage)"
        />
      </footer>
    </section>

    <v-dialog v-model="ZoneDetailDialog" persistent max-width="513px">
      <v-card class="zone-create-card">
        <header class="zone-create-header">
          <span>{{ EditStatus ? $t("infrastructure.editZone") : $t("infrastructure.createZone") }}</span>
          <button type="button" :aria-label="$t('infrastructure.closeZoneDialog')" @click="CloseDialog">
            <v-icon color="white" size="30">mdi-close</v-icon>
          </button>
        </header>

        <div class="zone-create-body">
          <div class="zone-create-field">
            <label for="zone-name">{{ $t("infrastructure.zoneName") }}</label>
            <v-text-field
              id="zone-name"
              v-model="ZoneName"
              :placeholder="$t('infrastructure.zoneName')"
              outlined
              dense
              hide-details
            />
          </div>
          <div class="zone-create-field mt-3">
            <label for="zone-name-th">{{ $t("infrastructure.zoneNameTH") }}</label>
            <v-text-field
              id="zone-name-th"
              v-model="ZoneNameTH"
              :placeholder="$t('infrastructure.zoneNameTH')"
              outlined
              dense
              hide-details
            />
          </div>
          <div class="zone-active-choice active-status-control" role="radiogroup" :aria-label="$t('infrastructure.zoneStatus')">
            <button
              :class="{ selected: String(Active) === '1' }"
              type="button"
              @click="Active = '1'"
            >
              {{ $t("infrastructure.active") }}
            </button>
            <button
              :class="{ selected: String(Active) === '2' }"
              type="button"
              @click="Active = '2'"
            >
              {{ $t("infrastructure.inactive") }}
            </button>
          </div>

          <footer class="zone-create-actions">
            <button class="cancel" type="button" @click="CloseDialog">{{ $t("infrastructure.cancel") }}</button>
            <button
              class="save"
              type="button"
              :disabled="IsSubmitting"
              @click="saveDataZone"
            >
              {{ $t("infrastructure.save") }}
            </button>
          </footer>
        </div>
      </v-card>
    </v-dialog>

    <v-dialog v-model="LoadingDialog" persistent width="auto">
      <v-card
        class="pa-6 d-flex flex-column align-center justify-center"
        elevation="8"
        rounded="xl"
      >
        <v-progress-circular
          indeterminate
          color="indigo"
          size="70"
          width="6"
        ></v-progress-circular>
        <span class="mt-4 text-subtitle-1 font-weight-medium"
          >{{ $t("infrastructure.loading") }}</span
        >
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from "axios";
import enurl from "@/api/environment";
import Swal from "sweetalert2/dist/sweetalert2.js";

export default {
  name: "Zone",
  components: {},
  data() {
    return {
      url: enurl.apiUrl,
      LoadingDialog: false,
      IsSubmitting: false,
      DeletingZoneID: null,
      items: [10, 15, 20, 50, 100],
      pageCount: 0,
      page: 1,
      itemsPerPage: 10,
      search: "",
      activeSearch: "",
      no_run: 0,

      ListData: [],
      ZoneID: null,
      ZoneNameTH: "",
      ZoneName: "",
      Active: 1,
      EditStatus: false,
      ZoneDetailDialog: false,
    };
  },
  computed: {
    statusItems() {
      return [
        { text: this.$t("infrastructure.active"), value: "Active" },
        { text: this.$t("infrastructure.inactive"), value: "InActive" },
      ];
    },
    headers() {
      return [
        {
          text: this.$t("message.No"),
          value: "no",
          sortable: false,
          align: "left",
        },
        {
          text: this.$t("infrastructure.zoneName"),
          value: "zoneName",
          align: "left",
          sortable: false,
        },
        {
          text: this.$t("infrastructure.zoneNameTH"),
          value: "zoneNameTH",
          align: "left",
          sortable: false,
        },
        {
          text: this.$t("infrastructure.status"),
          value: "active",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("infrastructure.action"),
          value: "action",
          align: "center",
          sortable: false,
        },
      ];
    },
  },

  async mounted() {
    let self = this;
    await self.getDataZoneList();
  },

  methods: {
    async getDataZoneList() {
      this.LoadingDialog = true;
      const temp = {
        search: this.search || "",
        page: this.page,
        perpage: this.itemsPerPage,
        active:
          this.activeSearch == "Active"
            ? true
            : this.activeSearch == "InActive"
            ? false
            : null,
      };
      try {
        const response = await axios.post(`${this.url}Zone/getDataZoneList`, temp);
        this.throwIfSessionExpired(response);
        if (response.data.status != 0) throw new Error(response.data.message);
        this.ListData = response.data.data.listData || [];
        this.pageCount = response.data.data.totalPages ?? response.data.data.count;
        this.no_run = (this.page - 1) * this.itemsPerPage;
      } catch (error) {
        await this.showRequestError(error);
      } finally {
        this.LoadingDialog = false;
      }
    },

    async ChangePage(value) {
      this.page = value;
      await this.getDataZoneList();
    },

    async ChangePerPage(value) {
      this.page = 1;
      this.itemsPerPage = value;
      await this.getDataZoneList();
    },

    async searchDataZone() {
      this.page = 1;
      await this.getDataZoneList();
    },

    getDataZoneByID(id) {
      let self = this;
      self.LoadingDialog = true;
      let temp = {
        zoneID: id
      };
      axios
        .post(`${self.url}Zone/getDataZoneByID`, temp)
        .then(function (response) {
          if (response.data.status == 0) {
            self.BindingData(response.data.data);
          }
        })
        .catch(function (error) {
          const message =
            error?.response?.data?.message ||
            error?.message ||
            self.$t("infrastructure.connectionError");

          Swal.fire({
            icon: "warning",
            width: 900,
            text: message,
          });
        })
        .finally(() => {
          self.LoadingDialog = false;
        });
    },

    validateZone() {
      if (!this.ZoneName || !this.ZoneName.trim()) return this.$t("infrastructure.zoneNameRequired");
      if (!this.ZoneNameTH || !this.ZoneNameTH.trim()) return this.$t("infrastructure.zoneNameTHRequired");
      if (this.ZoneName.trim().length > 200) return this.$t("infrastructure.zoneNameTooLong");
      if (this.ZoneNameTH.trim().length > 200) return this.$t("infrastructure.zoneNameTHTooLong");
      return "";
    },

    async saveDataZone() {
      if (this.IsSubmitting) return;
      const validationMessage = this.validateZone();
      if (validationMessage) {
        await Swal.fire({ icon: "warning", width: 500, text: validationMessage });
        return;
      }

      this.IsSubmitting = true;
      this.LoadingDialog = true;
      const payload = {
        zoneName: this.ZoneName.trim(),
        zoneNameTH: this.ZoneNameTH.trim(),
        active: String(this.Active) === "1",
      };
      if (this.EditStatus) payload.zoneID = this.ZoneID;

      try {
        const endpoint = this.EditStatus ? "Zone/editDataZone" : "Zone/addDataZone";
        const response = await axios.post(`${this.url}${endpoint}`, payload);
        this.throwIfSessionExpired(response);
        if (response.data.status != 0) throw new Error(response.data.message);
        await Swal.fire({
          icon: "success",
          title: this.EditStatus ? this.$t("infrastructure.updated") : this.$t("infrastructure.created"),
          text: this.EditStatus ? this.$t("infrastructure.updateSuccess") : this.$t("infrastructure.createSuccess"),
          showConfirmButton: false,
          timer: 1500,
        });
        if (!this.EditStatus) this.page = 1;
        this.CloseDialog();
        await this.getDataZoneList();
      } catch (error) {
        await this.showRequestError(error);
      } finally {
        this.IsSubmitting = false;
        this.LoadingDialog = false;
      }
    },

    async deleteDataZoneByID(zoneID) {
      if (this.DeletingZoneID !== null) return;
      const confirmation = await Swal.fire({
        icon: "warning",
        title: this.$t("infrastructure.deleteZoneTitle"),
        text: this.$t("infrastructure.deleteZoneText"),
        showCancelButton: true,
        confirmButtonText: this.$t("infrastructure.delete"),
        cancelButtonText: this.$t("infrastructure.cancel"),
        confirmButtonColor: "#d00000",
      });
      if (!confirmation.isConfirmed) return;

      this.DeletingZoneID = zoneID;
      this.LoadingDialog = true;
      try {
        const response = await axios.post(`${this.url}Zone/deleteDataZoneByID`, { zoneID });
        this.throwIfSessionExpired(response);
        if (response.data.status != 0) throw new Error(response.data.message);
        if (this.ListData.length === 1 && this.page > 1) this.page -= 1;
        await Swal.fire({ icon: "success", title: this.$t("infrastructure.deleted"), text: this.$t("infrastructure.deleteSuccess"), showConfirmButton: false, timer: 1500 });
        await this.getDataZoneList();
      } catch (error) {
        await this.showRequestError(error);
      } finally {
        this.DeletingZoneID = null;
        this.LoadingDialog = false;
      }
    },

    BindingData(data) {
      this.ZoneID = data.zoneID;
      this.ZoneName = data.zoneName;
      this.ZoneNameTH = data.zoneNameTH;
      this.Active = data.active == true ? "1" : data.active == false ? "2" : null;
      this.EditStatus = true;
      this.ZoneDetailDialog = true;
    },

    OpenDataDialog(type, id) {
      let self = this;
      if (type == "create") {
        self.ZoneID = null;
        self.ZoneNameTH = "";
        self.ZoneName = "";
        self.Active = "1";
        self.EditStatus = false;
        self.ZoneDetailDialog = true;
      } else {
        self.getDataZoneByID(id);
      }
    },

    CloseDialog() {
      this.ZoneID = null;
      this.ZoneNameTH = "";
      this.ZoneName = "";
      this.Active = "1";
      this.EditStatus = false;
      this.ZoneDetailDialog = false;
    },

    throwIfSessionExpired(response) {
      const message = String(response?.data?.message || "");
      if (message.toLowerCase().includes("session") && message.toLowerCase().includes("expired")) {
        const error = new Error(message);
        error.isSessionExpired = true;
        this.$store.dispatch("ForceLogout", message);
        throw error;
      }
    },

    async showRequestError(error) {
      if (error?.isSessionExpired || error?.response?.status === 401) return;
      await Swal.fire({
        icon: "warning",
        width: 900,
        text:
          error?.response?.data?.message ||
          error?.message ||
          this.$t("infrastructure.connectionError"),
      });
    },
  },
};
</script>
<style scoped>
@keyframes load {
  0% {
    transform: scaleY(0.4);
  }
  20% {
    transform: scaleY(1);
  }
  40% {
    transform: scaleY(0.4);
  }
  100% {
    transform: scaleY(0.4);
  }
}
@-moz-keyframes loader {
  from {
    transform: rotate(0);
  }
  to {
    transform: rotate(360deg);
  }
}
@-webkit-keyframes loader {
  from {
    transform: rotate(0);
  }
  to {
    transform: rotate(360deg);
  }
}
@-o-keyframes loader {
  from {
    transform: rotate(0);
  }
  to {
    transform: rotate(360deg);
  }
}
@keyframes loader {
  from {
    transform: rotate(0);
  }
  to {
    transform: rotate(360deg);
  }
}
.rect2 {
  animation-delay: -1.2s;
}
.rect3 {
  animation-delay: -1.1s;
}
.rect4 {
  animation-delay: -1s;
}
.rect5 {
  animation-delay: -0.9s;
}

.page-title {
  color: #444;
  font-size: 22px;
  margin-left: 1rem;
}

.cell {
  color: #333;
  font-size: 14px;
  padding: 10px 8px;
}

.data-table {
  color: #332f2f;
}

.v-data-table-header th {
  font-weight: 600 !important;
  font-size: 14px !important;
  background-color: #333 !important;
  color: white !important;
}

.rounded-lg {
  border-radius: 12px !important;
}

.rounded-xl {
  border-radius: 16px !important;
}

h1 {
  letter-spacing: 0.5px;
}

.v-text-field input {
  font-size: 15px;
}

.section-header {
  font-size: 16px;
  font-weight: 700;
  color: #1e1e1e;
  display: flex;
  align-items: center;
  margin-bottom: 4px;
}

.rounded-xl {
  border-radius: 16px !important;
}

.v-text-field input,
.v-select input {
  font-size: 15px;
}

.zone-page {
  min-height: calc(100vh - 125px);
  padding: var(--workspace-content-top) 0 60px;
  background: #f6f6f6;
}

.zone-create-card {
  min-height: 490px;
  overflow: hidden;
  border-radius: 16px !important;
  background: #ffffff;
}

.zone-create-header {
  position: relative;
  display: flex;
  height: 66px;
  align-items: center;
  justify-content: center;
  background: #00378e;
  color: #ffffff;
  font-size: 20px;
  font-weight: 600;
}

.zone-create-header button {
  position: absolute;
  top: 15px;
  right: 20px;
  display: grid;
  width: 36px;
  height: 36px;
  place-items: center;
  border: 0;
  background: transparent;
}

.zone-create-body {
  display: flex;
  min-height: 424px;
  flex-direction: column;
  padding: 22px 22px 28px;
}

.zone-create-field label {
  display: block;
  margin: 0 0 8px 2px;
  color: #252525;
  font-size: 13px;
  font-weight: 500;
}

.zone-create-field ::v-deep .v-input__slot {
  min-height: 50px !important;
  border-radius: 26px !important;
  background: #ffffff !important;
}

.zone-create-field ::v-deep .v-text-field__slot input {
  padding: 8px 4px;
  color: #282828;
  font-size: 14px;
}

.zone-create-field ::v-deep fieldset {
  border-color: #dddddd !important;
}

.zone-create-field ::v-deep .v-input--is-focused fieldset {
  border-color: #00378e !important;
}

.zone-active-choice {
  display: flex;
  gap: 22px;
  margin: 30px 0 0 20px;
}

.zone-active-choice button {
  width: 140px;
  height: 50px;
  border: 0;
  border-radius: 26px;
  background: #ffffff;
  box-shadow: 0 3px 8px rgba(0, 0, 0, 0.24);
  color: #282828;
  font-size: 14px;
}

.zone-active-choice button.selected {
  border: 3px solid #00378e;
  color: #282828;
  box-shadow: none;
}

.zone-create-actions {
  display: flex;
  justify-content: center;
  gap: 62px;
  margin-top: auto;
}

.zone-create-actions button {
  width: 170px;
  height: 60px;
  border: 0;
  border-radius: 30px;
  font-size: 16px;
}

.zone-create-actions .cancel {
  background: #eeeeee;
  color: #00378e;
  box-shadow: 0 3px 5px rgba(0, 0, 0, 0.22);
}

.zone-create-actions .save {
  background: #00378e;
  color: #ffffff;
  box-shadow: 0 3px 5px rgba(0, 0, 0, 0.22);
}

.zone-toolbar {
  display: grid;
  grid-template-columns: 385px minmax(360px, 660px) 157px;
  align-items: center;
  justify-content: center;
  gap: 23px;
  margin-bottom: 57px;
}

.zone-status-filter {
  border-radius: 60px;
}

.zone-status-filter ::v-deep .v-input__slot {
  min-height: 60px;
  padding: 0 20px 0 26px !important;
  border-radius: 60px !important;
  background: #ffffff !important;
  box-shadow: 0 8px 12px rgba(0, 0, 0, 0.2) !important;
}

.zone-status-filter ::v-deep .v-select__selection,
.zone-status-filter ::v-deep input {
  color: #595959;
  font-size: 18px;
}

.zone-search {
  display: flex;
  height: 60px;
  align-items: center;
  padding: 7px 9px 8px 36px;
  border-radius: 60px;
  background: #ffffff;
  box-shadow: 0 8px 12px rgba(0, 0, 0, 0.2);
}

.zone-search input {
  min-width: 0;
  flex: 1;
  border: 0;
  outline: 0;
  color: #191c1e;
  font-size: 20px;
}

.zone-search input::placeholder {
  color: #595959;
  opacity: 1;
}

.zone-search button {
  display: grid;
  width: 45px;
  height: 45px;
  flex: 0 0 45px;
  place-items: center;
  border: 0;
  border-radius: 50%;
  background: #00378e;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
}

.create-zone {
  height: 60px;
  border: 0;
  border-radius: 60px;
  background: #00378e;
  box-shadow: 0 8px 12px rgba(0, 0, 0, 0.2);
  color: #ffffff;
  font-size: 20px;
  font-weight: 500;
}

.zone-panel {
  position: relative;
  box-sizing: border-box;
  min-height: 436px;
  padding: 31px 18px 110px;
  border-radius: 30px;
  background: #ffffff;
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.2);
}

.zone-table-wrap {
  overflow-x: auto;
  border-radius: 30px 30px 0 0;
}

.zone-table {
  width: 100%;
  color: #4c4c4c;
  font-size: 15px;
}

.zone-table ::v-deep .v-data-table__wrapper {
  overflow-x: auto;
}

.zone-table ::v-deep table {
  min-width: 760px;
  table-layout: fixed;
}

.zone-table ::v-deep .v-data-table-header {
  background: #00378e;
}

.zone-table ::v-deep .v-data-table-header th {
  height: 81px;
  padding: 0 30px !important;
  border-bottom: 0 !important;
  color: #ffffff !important;
  font-size: 18px;
  font-weight: 400;
  white-space: nowrap;
}

.zone-table ::v-deep tbody td {
  height: 66px;
  padding: 0 30px !important;
  border-bottom: 0 !important;
  color: #4c4c4c;
  font-size: 15px;
}

.zone-table ::v-deep th:first-child,
.zone-table ::v-deep td:first-child {
  width: 12%;
}

.zone-table ::v-deep th:nth-child(2),
.zone-table ::v-deep td:nth-child(2),
.zone-table ::v-deep th:nth-child(3),
.zone-table ::v-deep td:nth-child(3) {
  width: 29%;
}

.zone-table ::v-deep th:nth-child(4),
.zone-table ::v-deep td:nth-child(4) {
  width: 16%;
  text-align: center;
}

.zone-table ::v-deep th:last-child,
.zone-table ::v-deep td:last-child {
  width: 14%;
  text-align: center;
}

.zone-table ::v-deep tbody tr {
  background: #f6f6f6;
}

.zone-table ::v-deep tbody tr:nth-child(even) {
  background: #ffffff;
}

.zone-table ::v-deep tbody tr:hover {
  background: #eef3fb !important;
}

.zone-empty {
  padding: 32px 16px;
  color: #595959;
  text-align: center;
}

.zone-status-chip {
  display: inline-flex;
  min-width: 94px;
  height: 32px;
  align-items: center;
  justify-content: center;
  border-radius: 18px;
  background: #91d589;
  color: #3f6840;
  font-size: 15px;
}

.zone-status-chip.inactive {
  background: #f1aaaa;
  color: #812424;
}

.zone-actions {
  display: flex;
  height: 66px;
  align-items: center;
  justify-content: center;
  gap: 26px;
}

.zone-actions button {
  display: grid;
  width: 36px;
  height: 36px;
  place-items: center;
  border: 0;
  border-radius: 50%;
  background: #ff8500;
}

.zone-actions button.delete {
  background: #d00000;
}

.zone-action-icon {
  display: block;
  object-fit: contain;
}

.zone-action-icon--edit {
  width: 18px;
  height: 18px;
}

.zone-action-icon--delete {
  width: 24px;
  height: 24px;
}

.zone-pagination {
  position: absolute;
  right: 18px;
  bottom: 33px;
  left: 18px;
  display: flex;
  height: 59px;
  align-items: center;
  justify-content: center;
}

.zone-page-size {
  position: absolute;
  right: 75px;
  width: 133px;
  max-width: 133px;
}

.zone-page-size ::v-deep .v-input__slot {
  min-height: 50px;
  border-radius: 30px !important;
  background: #f6f6f6 !important;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.25) !important;
}

.zone-pagination ::v-deep .v-pagination__item,
.zone-pagination ::v-deep .v-pagination__navigation {
  width: 49px;
  min-width: 49px;
  height: 49px;
  border-radius: 10px;
}

@media (max-width: 1200px) {
  .zone-page {
    padding-right: 24px;
    padding-left: 24px;
  }

  .zone-toolbar {
    grid-template-columns: 1fr 1.5fr 157px;
  }
}

@media (max-width: 760px) {
  .zone-page {
    padding: 24px 12px;
  }

  .zone-toolbar {
    grid-template-columns: 1fr;
    gap: 14px;
    margin-bottom: 28px;
  }

  .create-zone {
    width: 157px;
    justify-self: end;
  }

  .zone-panel {
    padding: 12px 12px 150px;
    border-radius: 16px;
  }

  .zone-pagination {
    bottom: 24px;
  }

  .zone-page-size {
    right: 50%;
    bottom: -66px;
    transform: translateX(50%);
  }

  .zone-create-card {
    min-height: 440px;
  }

  .zone-create-body {
    min-height: 374px;
    padding: 22px 18px 24px;
  }

  .zone-active-choice {
    gap: 12px;
    margin-left: 0;
  }

  .zone-active-choice button {
    width: 50%;
  }

  .zone-create-actions {
    gap: 12px;
  }

  .zone-create-actions button {
    width: 50%;
  }
}

/* Compact desktop density; dialog and mobile sizing remain unchanged. */
@media (min-width: 761px) {
  .zone-create-card {
    min-height: 441px;
    border-radius: 14px !important;
  }

  .zone-create-header {
    height: 59px;
    font-size: 18px;
  }

  .zone-create-header button {
    top: 13px;
    right: 18px;
    width: 32px;
    height: 32px;
  }

  .zone-create-body {
    min-height: 382px;
    padding: 20px 20px 25px;
  }

  .zone-create-field label {
    margin-bottom: 7px;
    font-size: 12px;
  }

  .zone-create-field ::v-deep .v-input__slot {
    min-height: 45px !important;
    border-radius: 23px !important;
  }

  .zone-create-field ::v-deep .v-text-field__slot input {
    font-size: 13px;
  }

  .zone-active-choice {
    gap: 20px;
    margin: 27px 0 0 18px;
  }

  .zone-active-choice button {
    width: 126px;
    height: 45px;
    border-radius: 23px;
    font-size: 13px;
  }

  .zone-create-actions {
    gap: 56px;
  }

  .zone-create-actions button {
    width: 153px;
    height: 54px;
    border-radius: 27px;
    font-size: 14px;
  }

  .zone-page {
    min-height: calc(100vh - 104px);
    padding: 36px 0;
  }

  .zone-toolbar {
    grid-template-columns: minmax(240px, 320px) minmax(360px, 560px) 126px;
    gap: 16px;
    margin-bottom: 28px;
  }

  .zone-status-filter ::v-deep .v-input__slot {
    min-height: 46px;
    padding: 0 17px !important;
    box-shadow: 0 3px 6px rgba(0, 0, 0, 0.16) !important;
  }

  .zone-status-filter ::v-deep .v-select__selection,
  .zone-status-filter ::v-deep input,
  .zone-search input {
    font-size: 15px;
  }

  .zone-search,
  .create-zone {
    height: 46px;
  }

  .zone-search {
    padding: 5px 6px 5px 22px;
    box-shadow: 0 3px 6px rgba(0, 0, 0, 0.16);
  }

  .zone-search button {
    width: 36px;
    height: 36px;
    flex-basis: 36px;
    box-shadow: none;
  }

  .create-zone {
    font-size: 16px;
  }

  .zone-panel {
    min-height: 360px;
    padding: 20px 14px 90px;
    border-radius: 16px;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.16);
  }

  .zone-table-wrap {
    border-radius: 12px 12px 0 0;
  }

  .zone-table ::v-deep .v-data-table-header th {
    height: 56px;
    padding: 0 20px !important;
    font-size: 15px;
  }

  .zone-table ::v-deep tbody td {
    height: 50px;
    padding: 0 20px !important;
    font-size: 13px;
  }

  .zone-status-chip {
    min-width: 78px;
    height: 26px;
    font-size: 13px;
  }

  .zone-actions {
    height: 50px;
    gap: 16px;
  }

  .zone-actions button {
    width: 34px;
    height: 34px;
  }

  .zone-pagination {
    right: 14px;
    bottom: 24px;
    left: 14px;
    height: 44px;
  }

  .zone-page-size {
    right: 30px;
    width: 112px;
    max-width: 112px;
  }

  .zone-page-size ::v-deep .v-input__slot {
    min-height: 42px;
  }

  .zone-pagination ::v-deep .v-pagination__item,
  .zone-pagination ::v-deep .v-pagination__navigation {
    width: 40px;
    min-width: 40px;
    height: 40px;
    border-radius: 8px;
  }
}
</style>
